/** Option lists the Organisation screen's forms share with its correction forms.
 *
 * Moved out of `Organisation.tsx` so the create and the correct forms offer **the same** choices:
 * two lists of jurisdictions is how a company can be created as one thing and corrected into
 * something the creation form never offered.
 */

import type { FacilityIn } from "../api/provisioning";

/** Where an entity or a site sits, in the terms that decide its factors.
 *
 * `OTHER` is offered and is refused by the server, deliberately: a jurisdiction EmitIQ cannot
 * price is a fact a customer should learn in one sentence at creation, not three months later from
 * an inventory that is mysteriously empty. Hiding it from the list would leave them wondering
 * whether their country was supported at all.
 */
export type JurisdictionValue = FacilityIn["jurisdiction"];
export type FacilityTypeValue = FacilityIn["facility_type"];

export const SITE_TYPES: readonly FacilityTypeValue[] = [
  "office",
  "manufacturing",
  "distribution",
  "retail",
  "other",
];

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** The lease treatments, in the words a person uses — **ADR-0034**.
 *
 * The classification turns on whether the asset is on the company's balance sheet, which the GHG
 * Protocol says twice to ask the company's accountant. So the labels say that rather than implying
 * the product can work it out.
 */
export const LEASE_LABEL: Record<string, string> = {
  owned: "Owned",
  finance_lease: "Finance or capital lease",
  operating_lease: "Operating lease",
  occupied_under_licence: "Occupied under licence",
  unknown: "Not answered",
};

/** The jurisdictions a site can be in, with the country each one means.
 *
 * **One table, so the country is data and not a conditional** (whole-product review 2026-11,
 * finding 11): the add-site form used to derive it with `jurisdiction === "GB" ? "GB" : ...`, a
 * branch on a market in the core form. A jurisdiction added to the API's closed set is a row here,
 * and `country` is required by the type so a row cannot be added without saying which country it
 * is. `ZZ` is the ISO "unknown or unspecified" code, for "somewhere else". */
export const JURISDICTIONS: ReadonlyArray<{
  value: JurisdictionValue;
  label: string;
  country: string;
}> = [
  { value: "GB", label: "United Kingdom", country: "GB" },
  { value: "US-CA", label: "United States — California", country: "US" },
  { value: "US", label: "United States — elsewhere", country: "US" },
  { value: "OTHER", label: "Somewhere else", country: "ZZ" },
];

/** The country a site's jurisdiction means. Unknown jurisdictions are `ZZ`, never a guess. */
export function countryOf(jurisdiction: JurisdictionValue): string {
  return JURISDICTIONS.find((entry) => entry.value === jurisdiction)?.country ?? "ZZ";
}

// The regimes a filing window can be declared for are not listed here (ADR-0205). They are the
// regime rows, read through `useRegimes()` in `../api/regimes`, which also supplies the label both
// the list of windows and the form that offers one print (the journey walk of 2026-10-31 found
// them disagreeing when each held its own table). A regime added as data appears in both.
